import React, { useEffect, useRef } from 'react';
import { DialogV2 } from '@withjoy/joykit';
import { Router } from './routes';
import { GetCheckoutDialogDataQuery } from '@graphql/generated';
import { useCheckoutFunnel } from './hooks/useCheckoutFunnel';
import { CheckoutFunnelProvider } from './CheckoutFunnelProvider';
import { CheckoutInitDataProvider, useGetCheckoutInitData } from './hooks/useCheckoutInitData';
import { CheckoutDialogProps } from './CheckoutDialog.types';
import { useCheckoutDialogContext } from '@apps/registry/guest/components/CheckoutDialog/CheckoutDialog.provider';

type UseInitMachineArgs = {
  data: GetCheckoutDialogDataQuery | undefined;
  isAffiliate: boolean;
  isGiftWrapPurchased: boolean;
};

const useInitMachine = ({ data, isAffiliate, isGiftWrapPurchased }: UseInitMachineArgs) => {
  const { route, setupMachine } = useCheckoutFunnel(ctx => [ctx.route]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current && route === 'setup' && data) {
      const { registryItem, order, orderMetadata } = data;
      setupMachine({
        isGiftWrapPurchased,
        registryItem,
        // registryOrder doesn't have platformType,
        // but `existingOrder` type is auto-generated and complains that registryOrder doesn't have this field
        // @ts-ignore
        existingOrder: isAffiliate
          ? data.registryOrder && orderMetadata
            ? { ...data.registryOrder, isPurchased: orderMetadata.isPurchased }
            : null
          : order && orderMetadata
            ? { ...order, isPurchased: orderMetadata.isPurchased }
            : null,
      });
      hasInitializedRef.current = true;
    }
  }, [route, data, setupMachine, isAffiliate, isGiftWrapPurchased]);
};

type DialogInternalProps = {
  registryItemId: string;
  currentOrderId: Maybe<string>;
  isAffiliate: boolean;
  isGiftWrapPurchased: boolean;
};
/**
 * When `Dialog`s are not open, its children are completely unmounted.
 * This separate internal component allows us to safely initialize/tear down the state machine.
 */
const DialogInternal = (props: DialogInternalProps) => {
  const { data } = useGetCheckoutInitData(props);
  useInitMachine({ data, isAffiliate: props.isAffiliate, isGiftWrapPurchased: props.isGiftWrapPurchased });

  return (
    <CheckoutInitDataProvider value={data}>
      <DialogV2.Body>
        <Router />
      </DialogV2.Body>
    </CheckoutInitDataProvider>
  );
};

export const CheckoutDialog = (props: CheckoutDialogProps) => {
  const { isOpen, onClose, registryItemId, currentOrderId, isAffiliate, isGroupGifting, isGiftWrapPurchased } = props;
  const { disableCloseOnOutsideClick } = useCheckoutDialogContext();

  return (
    <>
      <DialogV2
        isOpen={isOpen}
        onClose={onClose}
        size={isAffiliate || isGroupGifting ? '3xl' : '2xl'}
        overrides={{ Body: { props: { paddingX: 0, paddingY: 0 } } }}
        disableCloseOnOutsideClick={disableCloseOnOutsideClick}
      >
        <CheckoutFunnelProvider {...props}>
          {/* Dialog content is unmounted completely if  `isOpen=false`, so we'll check for
         `registryItemId` when evaluating `isOpen` higher up in the comp hierarchy  */}
          <DialogInternal
            registryItemId={registryItemId!}
            currentOrderId={currentOrderId}
            isAffiliate={isAffiliate}
            isGiftWrapPurchased={isGiftWrapPurchased}
          />
        </CheckoutFunnelProvider>
      </DialogV2>
    </>
  );
};
